import React from "react";
import { useTranslation } from "react-i18next";
import Container from "../Components/Container";
import TitleSection from "../Components/TitleSection";
import DateRowSelect from "../Components/DateRowSelect";
import { Button, Stack, Typography } from "@mui/material";
import { MoonStars, Sun, SunHorizon } from "@phosphor-icons/react";
import { orange, yellow } from "@mui/material/colors";
import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import { STEPS } from "../utils";
import { useRescheduling } from "../ReschedulingProvider";
import { getDateFnsLocale } from "utils/getDateFnsLocale";

const slotsDescription = {
  morning: { Icon: SunHorizon, color: orange[700] },
  afternoon: { Icon: Sun, color: yellow[700] },
  evening: { Icon: MoonStars, color: "#8E42CA" },
};

export default function CustomDate() {
  const { t } = useTranslation(["rescheduling"]);
  const { onBack, setStep, callInfo, clinicianAvailableAppointmentTimes, setNewAppointmentTime } =
    useRescheduling();
  const [selectedDate, setSelectedDate] = React.useState(callInfo?.scheduled_date);
  const [availableTimes, setAvailableTimes] = React.useState({
    morning: [],
    afternoon: [],
    evening: [],
  });
  const today = new Date();
  // Available dates rule is (now > 24 hours) and (schedule_date < 7 days)
  // but added 3 days to the start and end disabled to make some more room
  const startDate = addDays(today, -3);
  const endDate = addDays(new Date(callInfo?.scheduled_date), 10);

  const clinicianAvailableTimesMap = React.useMemo(() => {
    const map = {};
    clinicianAvailableAppointmentTimes.forEach((slot) => {
      const dateKey = format(parseISO(slot.startDateTime), "yyyy-MM-dd");
      if (!map[dateKey]) {
        map[dateKey] = [];
      }
      map[dateKey].push(slot);
    });
    return map;
  }, [clinicianAvailableAppointmentTimes]);

  const disabledDates = React.useMemo(() => {
    // Generate all dates in the range
    const totalDays = differenceInCalendarDays(endDate, startDate) + 1;
    const allDates = Array.from({ length: totalDays }, (_, i) => addDays(startDate, i));

    // Includes first 3 and last 3 dates of gap, and dates with no available times
    const emptyDates = allDates.filter((date) => {
      const dateKey = format(date, "yyyy-MM-dd");
      return (
        !clinicianAvailableTimesMap[dateKey] || clinicianAvailableTimesMap[dateKey].length === 0
      );
    });
    return emptyDates;
  }, [startDate, endDate, clinicianAvailableTimesMap]);

  React.useEffect(() => {
    if (Object.keys(clinicianAvailableTimesMap).length > 0) {
      const availableTimes = {
        morning: [],
        afternoon: [],
        evening: [],
      };
      const dateKey = format(parseISO(selectedDate), "yyyy-MM-dd");
      const clinicianAvailableAppointmentTimes = clinicianAvailableTimesMap[dateKey] || [];
      clinicianAvailableAppointmentTimes.forEach((slot) => {
        const startDateTime = new Date(slot.startDateTime);
        const hourValue = startDateTime.getHours();
        if (hourValue < 12) {
          availableTimes.morning.push(startDateTime);
        } else if (hourValue < 18) {
          availableTimes.afternoon.push(startDateTime);
        } else {
          availableTimes.evening.push(startDateTime);
        }
      });
      setAvailableTimes(availableTimes);
    }
  }, [selectedDate, clinicianAvailableTimesMap]);

  return (
    <Container onBack={onBack}>
      <TitleSection title={t("rescheduling:customDate.title")} />
      <Stack gap={7}>
        <DateRowSelect
          startDate={startDate}
          endDate={endDate}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          disabledDates={disabledDates}
          fullDates={[today]}
        />
        <Stack gap={10}>
          {Object.keys(availableTimes).map((key) => {
            const hasElements = availableTimes[key].length > 0;
            if (!hasElements) return null;
            const { Icon, color } = slotsDescription[key];
            const locale = getDateFnsLocale();
            return (
              <Stack key={key} gap={3}>
                <Stack direction="row" gap={4} alignItems="center">
                  <Icon weight="duotone" color={color} size="24px" />
                  <Typography variant="body2">{t(`rescheduling:customDate.${key}`)}</Typography>
                </Stack>
                {availableTimes[key].map((time) => (
                  <Button
                    key={time}
                    color="secondary"
                    onClick={() => {
                      setNewAppointmentTime(time);
                      setStep(STEPS.review);
                    }}
                  >
                    {format(new Date(time), "h:mm a", { locale })}
                  </Button>
                ))}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Container>
  );
}
